import { useState, useEffect } from 'react';
import { customerService, orderService, newsletterService, trackPurchase, trackProductView, isSupabaseAvailable } from '../lib/supabase';

// Hook for customer management
export function useCustomer() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createOrUpdateCustomer = async (customerData) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - customer management disabled');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Check if customer exists
      let existingCustomer = null;
      if (customerData.email) {
        existingCustomer = await customerService.getCustomerByEmail(customerData.email);
      }
      
      let result;
      if (existingCustomer) {
        // Update existing customer
        result = await customerService.updateCustomer(existingCustomer.id, customerData);
      } else {
        // Create new customer
        result = await customerService.createCustomer(customerData);
      }
      
      setCustomer(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getCustomerByEmail = async (email) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - customer management disabled');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await customerService.getCustomerByEmail(email);
      setCustomer(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    customer,
    loading,
    error,
    createOrUpdateCustomer,
    getCustomerByEmail
  };
}

// Hook for order management
export function useOrders(customerId = null) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createOrder = async (orderData) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - order tracking disabled');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const order = await orderService.createOrder(orderData);
      
      // Add order items
      if (orderData.items && orderData.items.length > 0) {
        const orderItems = orderData.items.map(item => ({
          order_id: order.id,
          product_id: item.productId,
          shopify_variant_id: item.variantId,
          quantity: item.quantity,
          unit_price: item.price,
          total_price: item.price * item.quantity
        }));
        
        await orderService.addOrderItems(orderItems);
      }
      
      // Track purchase analytics
      await trackPurchase({
        order_id: order.id,
        total_amount: orderData.total_amount,
        item_count: orderData.items?.length || 0,
        is_subscription: orderData.is_subscription || false
      });
      
      // Refresh orders if we have a customer ID
      if (customerId) {
        fetchCustomerOrders(customerId);
      }
      
      return order;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerOrders = async (customerId) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - order tracking disabled');
      return [];
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const customerOrders = await orderService.getCustomerOrders(customerId);
      setOrders(customerOrders);
      return customerOrders;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - order tracking disabled');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const updatedOrder = await orderService.updateOrderStatus(orderId, status);
      
      // Update orders list
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status } : order
        )
      );
      
      return updatedOrder;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders on mount if customerId is provided
  useEffect(() => {
    if (customerId) {
      fetchCustomerOrders(customerId);
    }
  }, [customerId]);

  return {
    orders,
    loading,
    error,
    createOrder,
    fetchCustomerOrders,
    updateOrderStatus
  };
}

// Hook for newsletter management
export function useNewsletter() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (email, name = null) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - newsletter disabled');
      setError('Newsletter service not available - please configure Supabase');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await newsletterService.subscribe(email, name);
      setSubscribed(true);
      return true;
    } catch (err) {
      // Check if already subscribed
      if (err.message.includes('duplicate key')) {
        setError('This email is already subscribed to our newsletter.');
      } else {
        setError(err.message);
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async (email) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - newsletter disabled');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await newsletterService.unsubscribe(email);
      setSubscribed(false);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async (email) => {
    if (!isSupabaseAvailable()) {
      console.warn('Supabase not configured - newsletter disabled');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const isSubscribed = await newsletterService.isSubscribed(email);
      setSubscribed(isSubscribed);
      return isSubscribed;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    subscribed,
    subscribe,
    unsubscribe,
    checkSubscription
  };
}

// Hook for analytics tracking
export function useAnalytics() {
  const trackView = async (productId) => {
    if (!isSupabaseAvailable()) return;
    
    try {
      await trackProductView(productId);
    } catch (err) {
      console.warn('Analytics tracking failed:', err);
    }
  };

  const trackCartAdd = async (productId, quantity = 1) => {
    if (!isSupabaseAvailable()) return;
    
    try {
      const { trackCartAction } = await import('../lib/supabase');
      await trackCartAction('add', productId, quantity);
    } catch (err) {
      console.warn('Analytics tracking failed:', err);
    }
  };

  const trackCartRemove = async (productId, quantity = 1) => {
    if (!isSupabaseAvailable()) return;
    
    try {
      const { trackCartAction } = await import('../lib/supabase');
      await trackCartAction('remove', productId, quantity);
    } catch (err) {
      console.warn('Analytics tracking failed:', err);
    }
  };

  return {
    trackView,
    trackCartAdd,
    trackCartRemove
  };
}